import L from 'leaflet'
import * as GeoTIFF from 'geotiff'

// Calculate Ground Sample Distance (GSD) from TIFF image
export const calculateGSD = (
  image: GeoTIFF.GeoTIFFImage,
  bbox: number[],
  geoKeys: any
): { gsd: number; details: any } => {
  const [minX, minY, maxX, maxY] = bbox
  const geographicWidth = maxX - minX
  const geographicHeight = maxY - minY
  const pixelWidth = image.getWidth()
  const pixelHeight = image.getHeight()

  // Check if using WGS84 (EPSG:4326)
  const isWGS84 = geoKeys && geoKeys.GeographicTypeGeoKey === 4326
  let gsd: number
  let details: any = {}

  if (isWGS84) {
    // For WGS84, convert degrees to meters
    const centerLat = (minY + maxY) / 2
    const METERS_PER_DEGREE_LAT = 111320
    const METERS_PER_DEGREE_LON = Math.cos((centerLat * Math.PI) / 180) * 111320

    // Convert geographic dimensions to meters
    const widthMeters = Math.abs(geographicWidth) * METERS_PER_DEGREE_LON
    const heightMeters = Math.abs(geographicHeight) * METERS_PER_DEGREE_LAT

    // Calculate GSD in meters/pixel
    const gsdXMeters = widthMeters / pixelWidth
    const gsdYMeters = heightMeters / pixelHeight
    gsd = Math.max(gsdXMeters, gsdYMeters)

    details = {
      method: 'WGS84',
      centerLat,
      widthMeters,
      heightMeters,
      gsdXMeters,
      gsdYMeters,
    }
  } else {
    // If not WGS84, use the raw resolution values
    const [gsdX, gsdY] = image.getResolution()
    gsd = Math.max(Math.abs(gsdX), Math.abs(gsdY))
    details = {
      method: 'raw',
      gsdX,
      gsdY,
    }
  }

  return { gsd, details }
}

// Create a debug overlay to display error information
export const createDebugOverlay = (
  map: L.Map,
  minLat: number,
  minLng: number,
  maxLat: number,
  maxLng: number,
  errorMessage: string
): L.Rectangle => {
  const bounds = L.latLngBounds(L.latLng(minLat, minLng), L.latLng(maxLat, maxLng))

  const rectangle = L.rectangle(bounds, {
    color: '#ff7800',
    weight: 2,
    fillOpacity: 0.3,
    fillColor: '#ff7800',
    interactive: true,
  })

  const center = bounds.getCenter()
  const marker = L.marker(center, {
    icon: L.divIcon({
      html: `<div style="background-color: rgba(0,0,0,0.7); color: white; padding: 5px; border-radius: 5px;">${errorMessage}</div>`,
      className: 'debug-marker',
    }),
  })

  rectangle.addTo(map)
  marker.addTo(map)

  return rectangle
}

// Get color for prediction class visualization
export const getClassColor = (className: string): string => {
  // Map different class names to colors
  const colorMap: Record<string, string> = {
    combatant: '#FF5733',
    cargo: '#33FF57',
    tanker: '#3357FF',
    fishing: '#FF33A8',
    passenger: '#33FFF5',
    tug: '#F533FF',
    default: '#FFFF33',
  }

  return colorMap[className] || colorMap['default']
}

// Draw prediction rectangles on a map
export const drawPredictions = (
  map: L.Map,
  layer: {
    id: string
    bounds: L.LatLngBounds
    metadata?: { width: number; height: number }
    overlay: L.ImageOverlay
    isVisible: boolean
  },
  predictions: any[]
): L.Rectangle[] => {
  if (!map || !layer.metadata || !layer.overlay) {
    console.error('Cannot draw predictions: missing map, metadata, or overlay')
    return []
  }

  // Get the image dimensions
  const imageWidth = layer.metadata.width
  const imageHeight = layer.metadata.height

  // Get the bounds of the layer
  const layerBounds = layer.bounds

  // Create rectangles for each prediction
  const predictionRectangles: L.Rectangle[] = []

  predictions.forEach((prediction) => {
    const { xmin, ymin, xmax, ymax } = prediction.bounding_boxes

    // Convert pixel coordinates to latLng coordinates
    // Note: For georeferenced imagery, y=0 is at the top, but latitude increases going north (up)
    const southWest = layerBounds.getSouthWest()
    const northEast = layerBounds.getNorthEast()

    const lngDiff = northEast.lng - southWest.lng
    const latDiff = northEast.lat - southWest.lat

    // Calculate the proportional position in the image
    // Adjust y-coordinate conversion since y=0 is at the top in image coordinates
    const proportionalXmin = xmin / imageWidth
    const proportionalXmax = xmax / imageWidth
    const proportionalYmin = 1 - ymin / imageHeight // Invert Y axis
    const proportionalYmax = 1 - ymax / imageHeight // Invert Y axis

    // Convert to map coordinates
    const rectSouthWest = L.latLng(
      southWest.lat + latDiff * proportionalYmax,
      southWest.lng + lngDiff * proportionalXmin
    )

    const rectNorthEast = L.latLng(
      southWest.lat + latDiff * proportionalYmin,
      southWest.lng + lngDiff * proportionalXmax
    )

    // Create the rectangle bounds
    const rectBounds = L.latLngBounds(rectSouthWest, rectNorthEast)

    // Create rectangle with class-specific styling
    const rectangle = L.rectangle(rectBounds, {
      color: getClassColor(prediction.class_name),
      weight: 2,
      opacity: 0.8,
      fillOpacity: 0.2,
      fillColor: getClassColor(prediction.class_name),
      className: `prediction-box ${prediction.class_name}`,
    })

    // Add tooltip with prediction info
    rectangle.bindTooltip(
      `<div class="prediction-tooltip">
        <strong>${prediction.class_name}</strong>
        <div>Confidence: ${(prediction.confidence * 100).toFixed(2)}%</div>
      </div>`,
      { sticky: true, className: 'prediction-tooltip' }
    )

    // Add the rectangle to the map if layer is visible
    if (layer.isVisible) {
      rectangle.addTo(map)
    }

    predictionRectangles.push(rectangle)
  })

  return predictionRectangles
}
